import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Button } from 'components/Button';
import ProgressBar from './ProgressBar/ProgressBar';
import * as Collapsible from '@radix-ui/react-collapsible';
import styles from './Program.module.scss';
import { PlayIcon } from './PlayIcon/PlayIcon';
import { useTranslation } from 'hooks/useTranslation';
import { Card } from '@yleisradio/areena-types';
import { findLabel } from 'utils/card';
import { Marker } from './Marker';
import ChevronRight from 'assets/chevronRight.svg';
import {
  ProgramControl,
  DisabledProgramControl,
} from './ProgramControl/ProgramControl';
import { determineBroadcastStatus } from '../utils';
import { getControlKey } from 'utils/control';
import { sanitizeHtmlId } from 'utils/ui-helpers';
import { SummaryViewEventParams } from 'services/snowplowAnalytics/contextGenerators';
import { randomUUID } from 'utils/uuid';
import { trackClick } from 'services/snowplowAnalytics/snowplowAnalytics';

interface ProgramProps {
  currentTime: number;
  listKey: string;
  program: Card;
  sendSummaryViewEvent: (params: SummaryViewEventParams) => void;
}

export const Program = ({
  currentTime,
  listKey,
  program,
  sendSummaryViewEvent,
}: ProgramProps) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const { title, description = '', labels = [], pointer } = program;
  const t = useTranslation();
  const [summaryViewUuid, setSummaryViewUuid] = useState<string | undefined>();

  useEffect(() => {
    if (infoOpen) {
      const summaryViewUuid = randomUUID();
      setSummaryViewUuid(summaryViewUuid);
      sendSummaryViewEvent({ viewUuid: summaryViewUuid, card: program });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoOpen]);

  const isActiveProgram =
    determineBroadcastStatus(program, currentTime) === 'current' || infoOpen;
  const onDemandStatus = findLabel(labels, 'ondemandStatus')?.raw;
  const programStartTimestamp = Date.parse(
    findLabel(labels, 'broadcastStartDate')?.raw || ''
  );
  const programEndTimestamp = Date.parse(
    findLabel(labels, 'broadcastEndDate')?.raw || ''
  );

  const idBase = sanitizeHtmlId(
    `program-${listKey}-${title}-${programStartTimestamp}`
  );
  const timeElementId = `${idBase}-time`;
  const titleElementId = `${idBase}-title`;

  const onClick = useCallback(
    (action: 'link' | 'play') => {
      return () => {
        if (summaryViewUuid) {
          trackClick({ action, viewUuid: summaryViewUuid });
        }
      };
    },
    [summaryViewUuid]
  );

  const controlsElement = (() => {
    const controls = [];
    if (pointer) {
      controls.push(
        <Button
          key="program-page"
          text={t('linkToProgramPage')}
          iconRight={ChevronRight}
          pointer={pointer}
          variant="text"
          padding="none"
          theme={isActiveProgram ? 'light' : undefined}
          onClick={onClick('link')}
        />
      );
    }

    if (program.controls) {
      controls.push(
        ...program.controls.map((control) => {
          return (
            <ProgramControl
              key={getControlKey(control)}
              control={control}
              onClick={onClick}
            />
          );
        })
      );
    }

    if (onDemandStatus === 'upcoming') {
      const tKey =
        findLabel(labels, 'mediaType')?.raw === 'audio'
          ? 'programOndemandAudioUpcoming'
          : 'programOndemandVideoUpcoming';

      controls.push(
        <DisabledProgramControl key="ondemand-upcoming" label={t(tKey)} />
      );
    }

    if (onDemandStatus === 'norights') {
      const tKey =
        findLabel(labels, 'mediaType')?.raw === 'audio'
          ? 'programAudioNoRights'
          : 'programVideoNoRights';

      controls.push(
        <em key="ondemand-norights" className={styles.noRights}>
          {t(tKey)}
        </em>
      );
    }

    return controls.length > 0 ? (
      <div className={styles.controlsContainer}>{controls}</div>
    ) : null;
  })();

  const timeElement = (() => {
    const startDateLabel = findLabel(labels, 'broadcastStartDate');

    return startDateLabel && startDateLabel.raw && startDateLabel.formatted ? (
      <time
        dateTime={startDateLabel.raw}
        className={styles.programTime}
        id={timeElementId}
      >
        {startDateLabel.formatted}
      </time>
    ) : null;
  })();

  const onDemandStatusIconElement = (() => {
    if (onDemandStatus === 'current' || onDemandStatus === 'upcoming') {
      return (
        <span className={styles.onDemandStatusIcon}>
          <PlayIcon
            aria-label={
              onDemandStatus === 'current'
                ? t('availableInAreena')
                : t('upcomingInAreena')
            }
            active={onDemandStatus === 'current'}
          />
        </span>
      );
    }

    return null;
  })();

  const videoFormatElement = (() => {
    const videoFormat = findLabel(labels, 'videoFormat');

    if (videoFormat?.formatted) return <Marker>{videoFormat.formatted}</Marker>;
    return null;
  })();

  const highlightElement = (() => {
    const highlight = findLabel(labels, 'highlight');

    if (highlight?.formatted) {
      return <Marker>{highlight.formatted}</Marker>;
    }

    return null;
  })();

  const hardOfHearingSubtitlingElement = (() => {
    const subtitling = findLabel(labels, 'hardOfHearingSubtitling');

    if (subtitling)
      return <span className={styles.infoMarker}>{subtitling.formatted}</span>;
    return null;
  })();

  const durationElement = (() => {
    const duration = findLabel(labels, 'duration');

    if (duration)
      return <span className={styles.infoMarker}>{duration.formatted}</span>;
    return null;
  })();

  const hasExpandableInfo =
    description ||
    hardOfHearingSubtitlingElement ||
    durationElement ||
    videoFormatElement ||
    controlsElement;

  return (
    <Collapsible.Root
      aria-labelledby={`${timeElementId} ${titleElementId}`}
      role="listitem"
      className={classNames(
        styles.programRoot,
        isActiveProgram && styles.programActive,
        !hasExpandableInfo && styles.programNoInfo
      )}
      open={infoOpen}
      onOpenChange={(open) => setInfoOpen(open)}
    >
      <Collapsible.Trigger className={styles.programHeader}>
        {timeElement}
        {onDemandStatusIconElement}
        <div className={styles.programHeaderTitleWrapper}>
          <h3 className={styles.programTitle} id={titleElementId}>
            {title}
          </h3>
          {highlightElement}
        </div>
        {determineBroadcastStatus(program, currentTime) === 'current' && (
          <ProgressBar
            start={programStartTimestamp}
            end={programEndTimestamp}
            current={currentTime}
          />
        )}
      </Collapsible.Trigger>

      <Collapsible.Content className={styles.programInfo}>
        {description} {hardOfHearingSubtitlingElement} {durationElement}{' '}
        {videoFormatElement}
        {controlsElement}
      </Collapsible.Content>
    </Collapsible.Root>
  );
};
